import type { FuzzyValue } from './fuzzyNumbers';
import { divideFuzzy, fractionToFuzzy, integerToFuzzy } from './fuzzyMath';
import type { Fraction } from './fractionType';
import { frac, parseFraction } from './integerMath';


export type Framerate = Fraction;

/**
 * Given a string of the form "50/1", returns an object
 * representing the framerate or undefined if we cannot parse it.
 */
export function parseFramerate(fps?: string): Framerate | undefined {
  if (!fps) {
    return undefined;
  }

  try {
    return parseFraction(fps);
  } catch (ex) {
    return undefined;
  }
}


/**
 * How many frame numbers drop-frame counting skips at each affected minute, or
 * null for a frame rate that has no drop-frame counting. Only 30000/1001 (two)
 * and 60000/1001 (four) have it; 24000/1001 is fractional NTSC with none.
 */
export function dropFrameSkipCount(framerate: Framerate): 2 | 4 | null {
  if (framerate.denominator !== 1001) return null;
  if (framerate.numerator === 30000) return 2;
  if (framerate.numerator === 60000) return 4;
  return null;
}


/**
 * Whether drop-frame counting is defined for a frame rate. Whether material is
 * in fact counted drop-frame is separate, and stated by the `;`/`:` delimiter
 * of the timecode it carries.
 */
export function supportsDropFrameCounting(framerate: Framerate): boolean {
  return dropFrameSkipCount(framerate) !== null;
}


export function framesToSeconds(frames: number, framerate: Framerate): number {
  return (frames / framerate.numerator) * framerate.denominator;
}


export function secondsToFrames(seconds: number, framerate: Framerate): number {
  return Math.round((seconds * framerate.numerator) / framerate.denominator);
}

export function getFrameDurationFromFrameRateApproximation(mediaFrameRate: number): FuzzyValue {
  if (Number.isInteger(mediaFrameRate)) {
    return fractionToFuzzy(frac(1, mediaFrameRate));
  }

  if (mediaFrameRate == 23.976 || mediaFrameRate == 23.98) {
    /*
      23.976 is the standard film/DCI frame rate, stored as 24000/1001. Media
      metadata reports it either as 23.976 or rounded to 23.98, and both spell
      the same 24000/1001 rate (e.g. clients/desktop-app/src/dynamic-main/dvr.ts
      and common/src/dto/editProxyFps.ts). It is already treated as a
      first-class rate elsewhere in the codebase (e.g. common/src/util/timecode.ts's
      fpsMapForTcLib), so it must resolve to a precise fraction here too,
      the same way 29.97 and 59.94 do below.
    */
    return { approximate: 1001 / 24000, accuracyWarning: 'Interpreted 23.976 as 24000/1001 FPS', precise: { numerator: 1001, denominator: 24000 } };
  }

  if (mediaFrameRate == 29.97) {
    /*
      According to statistical sampling around 1% of media files stored in Mimir
      has a 30000/1001 frame rate. The frame rate 2997/100 is so uncommon that
      it did not show up in any of the 10000 media files sampled.
    */
    return { approximate: 1001 / 30000, accuracyWarning: 'Interpreted 29.97 as 30000/1001 FPS', precise: { numerator: 1001, denominator: 30000 } };
  }

  if (mediaFrameRate == 59.94) {
    /*
      According to statistical sampling around 0.2% of media files stored in
      Mimir has a 60000/1001 frame rate. The frame rate 5994/100 is so uncommon
      that it did not show up in any of the 10000 media files sampled.
    */
    return { approximate: 1001 / 60000, accuracyWarning: 'Interpreted 59.94 as 60000/1001 FPS', precise: { numerator: 1001, denominator: 60000 } };
  }

  return { approximate: 1 / mediaFrameRate };
}

export function tryGetPreciseFrameRateFromApproximation(approximateFrameRate: number) {
  const frameDuration = getFrameDurationFromFrameRateApproximation(approximateFrameRate);
  const frameRate = divideFuzzy(integerToFuzzy(1), frameDuration);
  if (!frameRate.precise) return null;
  return frameRate.precise;
}
